import { Box, Grid, Avatar, Typography, TextField, Button } from "@mui/material";
import { Lock } from "@mui/icons-material";
import { useImpersonateLogin, useLogin } from "../../../api/useAuth";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { getLoginErrorMessages } from "../../../api/loginErrors";

type LoginProps = {
    setRecoverPassword: React.Dispatch<React.SetStateAction<boolean>>;
    isImpersonation?: boolean;
};

type LoginFormValues = {
    email: string;
    password: string;
    target_email: string;
};

export function LoginForm({ setRecoverPassword, isImpersonation = false }: LoginProps) {
    const login = useLogin();
    const impersonateLogin = useImpersonateLogin();
    const activeLogin = isImpersonation ? impersonateLogin : login;
    const errorMessages = activeLogin.isError ? getLoginErrorMessages(activeLogin.error) : [];

    const {
        handleSubmit,
        control,
        formState: { errors: fieldErrors },
    } = useForm<LoginFormValues>();

    const onSubmit: SubmitHandler<LoginFormValues> = async (formValues) => {
        if (isImpersonation) {
            impersonateLogin.mutate({ email: formValues.email, password: formValues.password, target_email: formValues.target_email });
            return;
        }
        login.mutate({ email: formValues.email, password: formValues.password });
    };

    return (
        <Grid item xs={12} sm={8} md={5} sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Box
                sx={{
                    my: 8,
                    mx: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                    <Lock />
                </Avatar>
                <Typography component="h1" variant="h5">
                    {isImpersonation ? "Entrar como outro utilizador" : "Entrar"}
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                    <Controller
                        name="email"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email"
                                name="email"
                                autoComplete="email"
                                autoFocus
                            />
                        )}
                    />
                    {fieldErrors.email && <Typography color="error">{fieldErrors.email.message}</Typography>}
                    <Controller
                        name="password"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                            />
                        )}
                    />
                    {fieldErrors.password && <Typography color="error">{fieldErrors.password.message}</Typography>}

                    {errorMessages.map((errorMessage) => (
                        <Typography color="error" key={errorMessage}>
                            {errorMessage}
                        </Typography>
                    ))}
                    {isImpersonation && (
                        <Controller
                            name="target_email"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="target_email"
                                    label="Email do utilizador"
                                    type="email"
                                    id="target_email"
                                    autoComplete="off"
                                />
                            )}
                        />
                    )}
                    <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                        Entrar
                    </Button>
                    <Button variant="text" onClick={() => setRecoverPassword(true)}>
                        Esqueceu a password? Clique aqui para fazer reset.
                    </Button>
                </Box>
            </Box>
        </Grid>
    );
}
